'use client';

import cookies from 'js-cookie';
import { registerContact } from '@/actions/hubspot/registerContact';
import { Button } from '@headlessui/react';
import { Content, KeyTextField, RichTextField } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { Fragment, useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { emailRegex, isCorporateEmail } from '@/utils';
import { NewsletterMailIcon } from '@/assets/NewsletterMailIcon';
import { ArrowRightIcon } from '@/assets';
import { LottiePlayer } from '@lottiefiles/lottie-player';

// Define a specific type for the Lottie Player element for type safety
interface LottiePlayerElement extends HTMLElement {
  play?: () => void;
  // You can add other Lottie Player methods here if needed in the future
}

// This comment disables the linter rule for this specific declaration,
// as augmenting JSX types is a recognized exception where 'namespace' is necessary.
declare module 'react' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'lottie-player': React.DetailedHTMLProps<
        React.HTMLAttributes<LottiePlayerElement> & {
          src?: string;
          background?: string;
          speed?: string;
          loop?: boolean;
          mode?: string;
          autoplay?: boolean;
        },
        LottiePlayerElement
      >;
    }
  }
}

interface IForm {
  email: string;
}

export enum FormStatus {
  DIRTY = 'DIRTY',
  SUBMITTED = 'SUBMITTED',
  FAILED = 'FAILED',
  WITH_ERRORS = 'WITH_ERRORS',
}

export type NewsLetterProps = SliceComponentProps<
  Content.NewsLetterSlice,
  Record<string, string>
>;

interface Props {
  hubspotPortalIdForm: string;
  formId: KeyTextField;
  variation: string;
  title: RichTextField | string;
  invite_text: RichTextField | string;
  disclaimer: RichTextField | string;
  email_placeholder: KeyTextField;
  slice_type?: string;
  button_label: KeyTextField;
}

export const BaseNewsletter = (props: Props): JSX.Element => {
  const {
    register,
    handleSubmit,
    reset,
    formState: {
      errors: { email: emailError },
      isSubmitting,
    },
  } = useForm<IForm>({ mode: 'onBlur' });
  const [status, setStatus] = useState<FormStatus>(FormStatus.DIRTY);

  const lottiePlayerRef = useRef<LottiePlayer | null>(null);

  // useEffect hook to dynamically import the lottie-player and play it.
  useEffect(() => {
    import('@lottiefiles/lottie-player');

    const player = lottiePlayerRef.current;

    if (!player) return;

    const playAnimation = () => {
      // Check if the play method exists before calling it
      if (player.play) {
        player.play();
      }
    };

    // The 'ready' event fires when the player is ready to be interacted with.
    player.addEventListener('ready', playAnimation);

    // Cleanup function to remove the event listener when the component unmounts.
    return () => {
      if (player) {
        player.removeEventListener('ready', playAnimation);
      }
    };
  }, []);

  const formSubmit = (values: IForm) => {
    reset();
    registerContact({
      properties: {
        email: values.email,
      },
      pageUri: `https://devsu.com${window.location.pathname}`,
      pageName: window.document.title,
      hubspotPortalIdForm: props.hubspotPortalIdForm,
      hubspotGuidForm: props.formId ?? '',
      hubspotutkCookie: cookies.get('hubspotutk'),
    })
      .then(() => {
        setStatus(FormStatus.SUBMITTED);
      })
      .catch((err) => {
        setStatus(FormStatus.FAILED);
        alert(err);
      });
  };

  const renderTitle = () => {
    if (props.variation === 'default') {
      return <div className="md:hidden pb-3 font-semibold">Subscribe</div>;
    }

    if (props.variation === 'blog-home-grid') {
      return (
        <div className="flex flex-col gap-4 items-start">
          <div className="pt-4">
            <NewsletterMailIcon width={48} height={56} />
          </div>
          <div className="text-start md:text-start text-white">
            {typeof props.title === 'string' ? (
              <h4>{props.title}</h4>
            ) : (
              <PrismicRichText field={props.title} />
            )}
          </div>
        </div>
      );
    }

    if (props.variation === 'forBlogEntry') {
      return (
        <div className="flex flex-col gap-y-[1rem] items-start">
          <div className="pt-4">
            <NewsletterMailIcon />
          </div>
          <div className="text-start md:text-start text-white">
            {typeof props.title === 'string' ? (
              <h4 className="text-2xl font-bold">{props.title}</h4>
            ) : (
              <PrismicRichText field={props.title} />
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="text-center md:text-start">
        {typeof props.title === 'string' ? (
          <h2>{props.title}</h2>
        ) : (
          <PrismicRichText field={props.title} />
        )}
      </div>
    );
  };

  const renderInvite = () => {
    if (props.variation === 'forBlogEntry') {
      return (
        <div className="text-sm text-center pb-6">
          <div>{props.invite_text.toString()}</div>
        </div>
      );
    }

    return (
      <div
        className={`text-sm md:text-base ${props.variation === 'forSubPages' ? 'text-center md:text-start' : ''}`}
      >
        {typeof props.invite_text === 'string' ? (
          <div>{props.invite_text}</div>
        ) : (
          <PrismicRichText field={props.invite_text} />
        )}
      </div>
    );
  };

  const getEnabledButtonClass = (isClicked: boolean) => {
    if (isClicked) {
      if (props.variation === 'forBlogEntry') {
        return 'border-none bg-white rounded-tr-lg rounded-br-lg rounded-tl-none rounded-bl-none';
      }

      if (props.variation === 'blog-home-grid') {
        return 'bg-button-gradient';
      }
      return 'bg-white';
    }

    if (props.variation === 'default') {
      return 'hover:bg-button-gradient rounded-r-lg rounded-l-lg rounded-b-lg rounded-t-none md:rounded-none md:rounded-r-lg';
    }

    if (props.variation === 'blog-home-grid') {
      return 'border-none button-filled-bright-green rounded-tr-lg rounded-br-lg rounded-tl-none rounded-bl-none';
    }

    if (props.variation === 'forBlogEntry') {
      return 'border-none button-filled-green-20 rounded-tr-lg rounded-br-lg rounded-tl-none rounded-bl-none';
    }

    return 'bg-primary-shade-green';
  };

  const getButtonClassName = () => {
    if (
      props.variation === 'blog-home-grid' ||
      props.variation === 'forBlogEntry'
    ) {
      return 'secondary-button mt-2';
    }

    return `flex flex-row justify-center items-center gap-x-6 h-12 ${props.variation === 'default' ? 'border-button-border bg-green-1' : 'border-primary-shade-green rounded-lg'}  border-1.5 border-solid  px-6 py-3 md:w-fit font-semibold  w-full`;
  };

  const buttonTextColor = `${props.variation === 'forBlogEntry' || props.variation === 'blog-home-grid' ? 'text-white' : 'text-black'}`;

  const renderSubmitButton = ({ active: clicking }: { active: boolean }) => {
    const enabledLabelClasses = clicking
      ? 'bg-button-gradient bg-clip-text text-transparent'
      : buttonTextColor;
    return (
      <button
        type="submit"
        disabled={isSubmitting}
        className={`${getButtonClassName()} ${isSubmitting ? 'bg-transparent' : getEnabledButtonClass(clicking)}`}
        aria-label="Submit newsletter form"
      >
        <span
          className={`${isSubmitting ? 'text-white/80' : enabledLabelClasses} flex flex-row items-center`}
        >
          {props.variation === 'blog-home-grid' ||
          props.variation === 'forBlogEntry' ? (
            <ArrowRightIcon className="arrow-icon" />
          ) : (
            ''
          )}
          {props.button_label}
        </span>
      </button>
    );
  };

  const getFormClassName = () => {
    if (props.variation === 'default') {
      return 'py-4 md:pt-6 md:pb-4 flex flex-col md:flex-row';
    }

    if (props.variation === 'blog-home-grid') {
      return 'pb-5 px-2 flex flex-col';
    }

    if (props.variation === 'forBlogEntry') {
      return 'pb-5 flex flex-col w-full';
    }
    return 'pb-5 gap-4 flex flex-col md:flex-row';
  };

  const getEmailInputClassName = () => {
    if (props.variation === 'default') {
      return 'border-white bg-transparent md:rounded-l-lg';
    }

    if (props.variation === 'blog-home-grid') {
      return 'border-1.5 rounded-lg bg-transparent placeholder:text-white';
    }

    if (props.variation === 'forBlogEntry') {
      return 'rounded-lg bg-transparent placeholder:text-white';
    }

    return 'border-1.5 rounded-lg';
  };

  const getEmailText = () => {
    if (props.variation === 'blog-home-grid') {
      return 'text-white';
    }

    if (props.variation === 'default') {
      return 'text-white';
    }

    return 'text-black';
  };

  const renderForm = () => {
    return (
      <form
        onSubmit={handleSubmit(formSubmit)}
        className={`${getFormClassName()}`}
      >
        <div className="flex flex-col w-full">
          <input
            className={`outline-none h-12 w-full p-3 border ${getEmailInputClassName()} ${status === FormStatus.WITH_ERRORS ? 'text-error' : getEmailText()}`}
            placeholder={props.email_placeholder ?? 'email'}
            {...register('email', {
              required: { value: true, message: 'Email is required' },
              pattern: { value: emailRegex, message: 'Enter a valid email' },
              validate: {
                isCorporate: (value) =>
                  isCorporateEmail(value) ||
                  'Please use a corporate email address',
              },
            })}
          />
          {emailError && (
            <span className="text-error text-sm mt-2 px-3">
              {emailError.message}
            </span>
          )}
          {status === FormStatus.SUBMITTED && (
            <div className="px-3 mt-2 text-primary-shade-green text-sm font-bold">
              THANK YOU FOR SUBSCRIBING
            </div>
          )}
        </div>
        <Button as={Fragment} type="submit">
          {renderSubmitButton}
        </Button>
      </form>
    );
  };

  const renderDisclaimer = () => {
    return (
      <div
        className={`[&_a]:underline text-xs ${props.variation === 'forSubPages' ? 'text-center md:text-start' : ''}`}
      >
        {typeof props.disclaimer === 'string' ? (
          <div>{props.disclaimer}</div>
        ) : (
          <PrismicRichText field={props.disclaimer} />
        )}
      </div>
    );
  };

  const getContainerClass = () => {
    if (props.variation === 'default') {
      // Fixed height to prevent any layout shift during form/animation mounting
      return 'flex flex-col tracking-tight text-white h-[320px]';
    }
    if (props.variation === 'forSubPages') {
      return 'flex flex-col tracking-tight md:flex-row md:gap-20 text-black justify-center gap-2 mx-5 my-16 h-[280px]';
    }
    // Add relative and overflow-hidden to contain the absolute positioned lottie player
    if (props.variation === 'blog-home-grid') {
      return 'relative overflow-hidden flex flex-col tracking-tight justify-between w-full py-6 px-4 gap-4 rounded-[20px] h-full';
    }
    if (props.variation === 'forBlogEntry') {
      return 'relative overflow-hidden flex flex-col justify-start w-full py-[1rem] px-[1rem] rounded-[20px] h-[280px]';
    }
    return 'flex flex-col tracking-tight justify-center max-w-[768px] w-full mx-auto gap-6 h-[300px]';
  };

  return (
    <section
      data-slice-type={props.slice_type}
      data-slice-variation={props.variation}
      className={getContainerClass()}
    >
      {/* Conditionally render the Lottie player as a background */}
      {(props.variation === 'blog-home-grid' ||
        props.variation === 'forBlogEntry') && (
        <lottie-player
          ref={lottiePlayerRef}
          loop
          mode="normal"
          background="transparent"
          src="https://cdn.lottielab.com/l/DYGno7ckLCe8SC.json"
          style={{
            position: 'absolute',
            width: '300%',
            height: '300%',
            top: '-100%',
            left: '-100%',
            zIndex: 0,
          }}
        />
      )}
      <div
        className={`z-10 flex flex-col ${props.variation === 'blog-home-grid' ? 'gap-y-3' : props.variation !== 'forBlogEntry' ? 'gap-y-5' : 'gap-y-[1.5rem]'}  ${props.variation !== 'forBlogEntry' ? 'justify-start' : 'justify-start items-start'}`}
      >
        {renderTitle()}
        {renderInvite()}
      </div>
      <div
        className={`z-10 flex flex-col ${props.variation !== 'forBlogEntry' ? 'justify-start' : 'justify-center items-start'} ${props.variation === 'blog-home-grid' ? 'mt-auto' : ''}`}
      >
        {renderForm()}
        {renderDisclaimer()}
      </div>
    </section>
  );
};
